import { CONTRACTS_VERSION, type ScalarValue, type SemanticModelInput } from '@aibi/contracts';

/**
 * Modelos semanticos de demostracion para explorar la UX sin backend. Solo describen
 * estructura (tablas, dimensiones, medidas, jerarquias): no contienen conexiones,
 * credenciales ni datos reales.
 */

const RETAIL_ID = 'demo.retail';
const SUPPORT_ID = 'demo.support';

type ColumnInput = NonNullable<SemanticModelInput['tables']>[number]['columns'][number];

const column = (
  table: string,
  name: string,
  label: string,
  dataType: 'string' | 'integer' | 'decimal' | 'date',
  extra: Pick<ColumnInput, 'semanticType' | 'hidden'> = {},
): ColumnInput => ({ id: `${table}.${name}`, name: label, sourceColumn: name, dataType, ...extra });

export const retailDemoModel: SemanticModelInput = {
  schemaVersion: CONTRACTS_VERSION,
  id: RETAIL_ID,
  name: 'Ventas retail (demo)',
  description: 'Pedidos, clientes y productos de una cadena minorista ficticia.',
  datasetId: 'demo.retail.dataset',
  tables: [
    {
      id: 'orders',
      name: 'Pedidos',
      datasetTableId: 'orders',
      columns: [
        column('orders', 'order_id', 'Pedido', 'integer', {
          semanticType: 'identifier',
          hidden: true,
        }),
        column('orders', 'order_date', 'Fecha', 'date', { semanticType: 'date' }),
        column('orders', 'customer_id', 'Cliente', 'integer', { hidden: true }),
        column('orders', 'product_id', 'Producto', 'integer', { hidden: true }),
        column('orders', 'channel', 'Canal', 'string'),
        column('orders', 'amount', 'Importe', 'decimal', { semanticType: 'currency' }),
        column('orders', 'quantity', 'Unidades', 'integer', { semanticType: 'quantity' }),
      ],
    },
    {
      id: 'customers',
      name: 'Clientes',
      datasetTableId: 'customers',
      columns: [
        column('customers', 'customer_id', 'Cliente', 'integer', { semanticType: 'identifier' }),
        column('customers', 'region', 'Region', 'string'),
        column('customers', 'country', 'Pais', 'string', { semanticType: 'geo.country' }),
        column('customers', 'city', 'Ciudad', 'string', { semanticType: 'geo.city' }),
        column('customers', 'segment', 'Segmento', 'string'),
      ],
    },
    {
      id: 'products',
      name: 'Productos',
      datasetTableId: 'products',
      columns: [
        column('products', 'product_id', 'Producto', 'integer', { semanticType: 'identifier' }),
        column('products', 'category', 'Categoria', 'string'),
        column('products', 'subcategory', 'Subcategoria', 'string'),
      ],
    },
  ],
  dimensions: [
    {
      id: 'dim.order_date',
      name: 'Fecha de pedido',
      description: 'Fecha en que se registro el pedido.',
      columnId: 'orders.order_date',
      role: 'temporal',
      defaultTimeGrain: 'month',
    },
    {
      id: 'dim.region',
      name: 'Region',
      columnId: 'customers.region',
      role: 'geographic',
      hierarchyId: 'geography',
    },
    {
      id: 'dim.country',
      name: 'Pais',
      description: 'Pais de residencia del cliente.',
      synonyms: ['nacion'],
      columnId: 'customers.country',
      role: 'geographic',
      hierarchyId: 'geography',
    },
    {
      id: 'dim.city',
      name: 'Ciudad',
      columnId: 'customers.city',
      role: 'geographic',
      hierarchyId: 'geography',
    },
    {
      id: 'dim.segment',
      name: 'Segmento',
      description: 'Segmento comercial del cliente.',
      columnId: 'customers.segment',
      role: 'categorical',
    },
    {
      id: 'dim.channel',
      name: 'Canal',
      description: 'Canal de venta del pedido.',
      columnId: 'orders.channel',
      role: 'categorical',
    },
    {
      id: 'dim.category',
      name: 'Categoria',
      columnId: 'products.category',
      role: 'categorical',
      hierarchyId: 'product',
    },
    {
      id: 'dim.subcategory',
      name: 'Subcategoria',
      columnId: 'products.subcategory',
      role: 'categorical',
      hierarchyId: 'product',
    },
  ],
  measures: [
    {
      id: 'total_sales',
      name: 'Ventas',
      description: 'Suma del importe de los pedidos.',
      aggregation: 'sum',
      columnId: 'orders.amount',
      format: { type: 'currency', currency: 'USD', decimals: 0 },
    },
    {
      id: 'units',
      name: 'Unidades',
      description: 'Unidades vendidas.',
      aggregation: 'sum',
      columnId: 'orders.quantity',
      format: { type: 'number', decimals: 0, thousandsSeparator: true },
    },
    {
      id: 'order_count',
      name: 'Pedidos',
      description: 'Cantidad de pedidos.',
      aggregation: 'count',
      tableId: 'orders',
      format: { type: 'number', decimals: 0, thousandsSeparator: true },
    },
  ],
  calculatedMeasures: [
    {
      id: 'avg_ticket',
      name: 'Ticket promedio',
      description: 'Ventas divididas entre pedidos.',
      formula: 'DIVIDE([Ventas], [Pedidos])',
      expression: {
        type: 'function',
        name: 'divide',
        args: [
          { type: 'measure', measureId: 'total_sales' },
          { type: 'measure', measureId: 'order_count' },
        ],
      },
      format: { type: 'currency', currency: 'USD', decimals: 2 },
    },
  ],
  relationships: [
    {
      id: 'orders_customers',
      fromColumnId: 'orders.customer_id',
      toColumnId: 'customers.customer_id',
      cardinality: 'many-to-one',
    },
    {
      id: 'orders_products',
      fromColumnId: 'orders.product_id',
      toColumnId: 'products.product_id',
      cardinality: 'many-to-one',
    },
  ],
  hierarchies: [
    {
      id: 'geography',
      name: 'Geografia',
      levels: [
        { id: 'geography.region', name: 'Region', dimensionId: 'dim.region' },
        { id: 'geography.country', name: 'Pais', dimensionId: 'dim.country' },
        { id: 'geography.city', name: 'Ciudad', dimensionId: 'dim.city' },
      ],
    },
    {
      id: 'product',
      name: 'Producto',
      levels: [
        { id: 'product.category', name: 'Categoria', dimensionId: 'dim.category' },
        { id: 'product.subcategory', name: 'Subcategoria', dimensionId: 'dim.subcategory' },
      ],
    },
  ],
  settings: { locale: 'es', currency: 'USD', primaryDateDimensionId: 'dim.order_date' },
};

export const supportDemoModel: SemanticModelInput = {
  schemaVersion: CONTRACTS_VERSION,
  id: SUPPORT_ID,
  name: 'Soporte (demo)',
  description: 'Tickets de soporte de una mesa de ayuda ficticia.',
  datasetId: 'demo.support.dataset',
  tables: [
    {
      id: 'tickets',
      name: 'Tickets',
      datasetTableId: 'tickets',
      columns: [
        column('tickets', 'ticket_id', 'Ticket', 'integer', {
          semanticType: 'identifier',
          hidden: true,
        }),
        column('tickets', 'opened_at', 'Apertura', 'date', { semanticType: 'date' }),
        column('tickets', 'priority', 'Prioridad', 'string'),
        column('tickets', 'team', 'Equipo', 'string'),
        column('tickets', 'status', 'Estado', 'string'),
        column('tickets', 'resolution_hours', 'Horas de resolucion', 'decimal'),
      ],
    },
  ],
  dimensions: [
    {
      id: 'dim.opened_at',
      name: 'Fecha de apertura',
      columnId: 'tickets.opened_at',
      role: 'temporal',
      defaultTimeGrain: 'month',
    },
    { id: 'dim.priority', name: 'Prioridad', columnId: 'tickets.priority', role: 'categorical' },
    { id: 'dim.team', name: 'Equipo', columnId: 'tickets.team', role: 'categorical' },
    { id: 'dim.status', name: 'Estado', columnId: 'tickets.status', role: 'categorical' },
  ],
  measures: [
    {
      id: 'ticket_count',
      name: 'Tickets',
      aggregation: 'count',
      tableId: 'tickets',
      format: { type: 'number', decimals: 0, thousandsSeparator: true },
    },
    {
      id: 'avg_resolution',
      name: 'Resolucion promedio (h)',
      aggregation: 'avg',
      columnId: 'tickets.resolution_hours',
      format: { type: 'number', decimals: 1, suffix: ' h' },
    },
  ],
  settings: { locale: 'es', primaryDateDimensionId: 'dim.opened_at' },
};

/** Miembros de ejemplo por dimension para el generador de datos de preview. */
export const demoPreviewMembers: Readonly<Record<string, readonly ScalarValue[]>> = {
  'dim.region': ['Norteamerica', 'Sudamerica', 'Europa', 'Asia Pacifico'],
  'dim.country': [
    'Mexico',
    'Estados Unidos',
    'Canada',
    'Brasil',
    'Argentina',
    'Espana',
    'Alemania',
    'Japon',
  ],
  'dim.city': [
    'CDMX',
    'Monterrey',
    'Austin',
    'Toronto',
    'Sao Paulo',
    'Buenos Aires',
    'Madrid',
    'Berlin',
    'Tokio',
  ],
  'dim.segment': ['Consumo', 'Corporativo', 'Pyme'],
  'dim.channel': ['Tienda', 'Online', 'Distribuidor'],
  'dim.category': ['Tecnologia', 'Hogar', 'Oficina', 'Deportes'],
  'dim.subcategory': [
    'Laptops',
    'Telefonos',
    'Muebles',
    'Cocina',
    'Papeleria',
    'Sillas',
    'Bicicletas',
  ],
  'dim.priority': ['Critica', 'Alta', 'Media', 'Baja'],
  'dim.team': ['Infraestructura', 'Aplicaciones', 'Mesa de ayuda'],
  'dim.status': ['Abierto', 'En curso', 'Resuelto', 'Cerrado'],
};

export const DEMO_MODELS: readonly SemanticModelInput[] = [retailDemoModel, supportDemoModel];
